import { Helmet } from 'react-helmet';
import i18n from '../i18n';

const BASE_URL = 'https://eventhappy.it';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;

const DEFAULT_TITLE_IT = 'Happy Events – Trova eventi, concerti e serate nella tua città';
const DEFAULT_TITLE_EN = 'Happy Events – Find events, concerts & nightlife in Italy';
const DEFAULT_DESC_IT = 'Scopri eventi, concerti, serate e attività vicino a te. Partecipa, condividi storie e incontra persone con Happy Events — la tua app per il divertimento.';
const DEFAULT_DESC_EN = 'Discover events, concerts, shows and activities near you in Italy. Attend, share stories and meet people with Happy Events — your guide to fun in Italy.';
const DEFAULT_KW_IT = 'eventi, concerti, serate, attività, divertimento, happy events, eventi vicino a me, cosa fare stasera';
const DEFAULT_KW_EN = 'events in Italy, Italian events, things to do in Italy, concerts Italy, nightlife Italy, events near me Italy, what to do in Italy, happy events';

const SEO = ({
  title,
  description,
  keywords,
  image = DEFAULT_IMAGE,
  url = BASE_URL,
  type = 'website',
  noindex = false,
  structuredData = null,
  lang: langProp,
}) => {
  const lang = langProp || i18n.language || 'it';
  const isEn = lang === 'en';

  const finalTitle = title || (isEn ? DEFAULT_TITLE_EN : DEFAULT_TITLE_IT);
  const finalDesc = description || (isEn ? DEFAULT_DESC_EN : DEFAULT_DESC_IT);
  const finalKw = keywords || (isEn ? DEFAULT_KW_EN : DEFAULT_KW_IT);
  const ogLocale = isEn ? 'en_GB' : 'it_IT';
  const ogLocaleAlt = isEn ? 'it_IT' : 'en_GB';

  const imageUrl = image.startsWith('http') ? image : `${BASE_URL}${image}`;
  const canonicalUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`;
  const robots = noindex ? 'noindex, nofollow' : 'index, follow';

  return (
    <Helmet>
      <html lang={lang} />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      <meta name="keywords" content={finalKw} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      {/* hreflang — tells Google both language versions exist */}
      <link rel="alternate" hreflang="it" href={canonicalUrl} />
      <link rel="alternate" hreflang="en" href={canonicalUrl} />
      <link rel="alternate" hreflang="x-default" href={BASE_URL} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Happy Events" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={ogLocaleAlt} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={finalTitle} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@happyevents" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={finalTitle} />

      {/* Structured Data */}
      {structuredData && (Array.isArray(structuredData) ? structuredData : [structuredData]).map((sd, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(sd)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
